import type React from "react";
import { useState } from "react";

import { faUser } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import invariant from "ts-invariant";

import { usePutTherapistMutation } from "api/hooks/usePutTherapistMutation";
import { useProfileContext } from "contexts/ProfileContext";
import { BaseButton } from "shared/atoms/BaseButton";
import { Dropdown } from "shared/atoms/inputs/Dropdown";
import { StyledInput } from "shared/atoms/inputs/StyledInputs";
import { Notification } from "shared/atoms/Notification";
import { WidgetBox } from "shared/containers/WidgetBox/WidgetBox";
import { theme } from "theme";
import { museoSansFont } from "theme/muiTheme";
import type { Market } from "types";
import { useLocalizedDate } from "utils/date";
import { getLngDropdownOpts } from "utils/locale/getLngDropdownOpts";
import { setStorageValue } from "utils/storage";

import {
  ButtonWrapper,
  EditFieldContainer,
  InfoTable,
  StyledParagraph,
  StyledTableRow,
  fieldWithBorder,
} from "../shared/styles";

type FormData = {
  first_name: string;
  last_name: string;
  language_code?: string;
  address: string;
  birth_year: string;
  birth_month: string;
  birth_date: string;
  city: string;
  phone_number: string;
  zip_code: string;
};

export const ProfileInformation: React.FC = () => {
  const { i18n, t } = useTranslation();
  const { profile, setProfile } = useProfileContext();
  const { parse, formatISO, set, getDaysInMonth, format } = useLocalizedDate();
  const putTherapistMutation = usePutTherapistMutation();

  invariant(profile);

  const birthday = profile.therapist_profile.birthday
    ? parse(profile.therapist_profile.birthday, "yyyy-MM-dd", new Date())
    : null;

  const { handleSubmit, register, watch, reset } = useForm({
    defaultValues: {
      first_name: profile.first_name || "",
      last_name: profile.last_name || "",
      address: profile.therapist_profile.address || "",
      zip_code: profile.therapist_profile.zip_code || "",
      city: profile.therapist_profile.city || "",
      phone_number: profile.therapist_profile.phone_number || "",
      birth_year: birthday?.getFullYear().toString() || t("common.year"),
      birth_month: birthday?.getMonth().toString() || t("common.month"),
      birth_date: birthday?.getDate().toString() || t("common.day"),
      language_code: profile.language_code || undefined,
    },
  });
  const [isEditing, setIsEditing] = useState(false);

  const watchBirthYear = watch("birth_year");
  const watchBirthMonth = watch("birth_month");
  const watchBirthDate = watch("birth_date");

  const handleChangePersonalInformationClick = () => {
    setIsEditing(!isEditing);
    putTherapistMutation.reset();
  };

  const REGION = profile.market;
  const dropdownValues = getLngDropdownOpts(REGION as Market);

  const formatBirthday = ({ birth_date, birth_month, birth_year }: FormData): string | undefined => {
    if ([birth_date, birth_month, birth_year].some(el => Number.isNaN(parseInt(el, 10)))) return undefined;
    return formatISO(
      set(new Date(), {
        year: parseInt(birth_year, 10),
        month: parseInt(birth_month, 10),
        date: parseInt(birth_date, 10),
        hours: 0,
        minutes: 0,
        seconds: 0,
      })
    );
  };

  const onSubmit = async (profileDataFromForm: FormData) => {
    const birthdayInUTC = formatBirthday(profileDataFromForm);
    const therapistId = profile.id;
    const data = {
      email: profile.email ?? "",
      language_code: profileDataFromForm.language_code ? profileDataFromForm.language_code : profile.language_code,
      first_name: profileDataFromForm.first_name ? profileDataFromForm.first_name : profile.first_name ?? "",
      last_name: profileDataFromForm.last_name ? profileDataFromForm.last_name : profile.last_name ?? "",
      therapist_profile: {
        address: profileDataFromForm.address === "" ? profile.therapist_profile.address : profileDataFromForm.address,
        birthday: birthdayInUTC || (profile.therapist_profile.birthday ?? null),
        city: profileDataFromForm.city === "" ? profile.therapist_profile.city : profileDataFromForm.city,
        phone_number:
          profileDataFromForm.phone_number === ""
            ? profile.therapist_profile.phone_number
            : profileDataFromForm.phone_number,
        zip_code:
          profileDataFromForm.zip_code === "" ? profile.therapist_profile.zip_code : profileDataFromForm.zip_code,
      },
    };

    const variables = { data, therapistId };

    putTherapistMutation.mutate(variables, {
      onSuccess(response) {
        setProfile(response);

        reset({
          first_name: data.first_name || "",
          last_name: data.last_name || "",
          address: data.therapist_profile.address || "",
          zip_code: data.therapist_profile.zip_code || "",
          city: data.therapist_profile.city || "",
          phone_number: data.therapist_profile.phone_number || "",
        });
        setIsEditing(!isEditing);

        const newLanguage = response.locale;

        if (newLanguage && i18n.language !== newLanguage) {
          setStorageValue("language", newLanguage);
          i18n.changeLanguage(newLanguage);
        }
      },
    });
  };

  const getDropdownYears = () => {
    const currentYear = new Date().getFullYear();
    return Array(119)
      .fill(0)
      .map((_x, i) => {
        return { value: (currentYear - i).toString(), label: (currentYear - i).toString() };
      })
      .reverse();
  };

  const getDropdownMonths = () => {
    return Array(12)
      .fill(0)
      .map((_x, i) => {
        return { value: String(i), label: format(set(new Date(), { month: i }), "LLL") };
      });
  };

  const getDropdownDays = () => {
    const today = new Date();
    const daysInMonth =
      watchBirthYear && watchBirthYear !== t("common.year") && watchBirthMonth && watchBirthMonth !== t("common.month")
        ? getDaysInMonth(
            set(today, {
              year: parseInt(watchBirthYear, 10),
              month: parseInt(watchBirthMonth, 10),
            })
          )
        : 31;
    return Array(daysInMonth)
      .fill(0)
      .map((_x, i) => {
        return { value: (i + 1).toString(), label: (i + 1).toString() };
      });
  };

  return (
    <WidgetBox
      data-testid="profile-information"
      icon={<FontAwesomeIcon icon={faUser} />}
      title={t("settings.profile_information.details")}
      color="blue"
      toolbar={
        !isEditing ? (
          <BaseButton
            onClick={handleChangePersonalInformationClick}
            dataTestID="edit-profile-information-button"
            variant="action"
            icon="pen"
            text={t("settings.profile_information.change_profile_information")}
            size="large"
          />
        ) : undefined
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} data-testid="profile-information">
        {putTherapistMutation.isError && (
          <Notification variant="danger" style={{ margin: "5px 0" }}>
            {t("errors.generic")}
          </Notification>
        )}

        <InfoTable>
          {isEditing ? (
            <>
              <EditFieldContainer>
                <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                  {t("form.first_name")}
                </Typography>
                <PersonalInformationInput data-testid="firstname-input" {...register("first_name")} />
              </EditFieldContainer>

              <EditFieldContainer>
                <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                  {t("form.last_name")}
                </Typography>
                <PersonalInformationInput data-testid="lastname-input" {...register("last_name")} />
              </EditFieldContainer>

              <EditFieldContainer>
                <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                  {t("settings.profile_information.address")}
                </Typography>
                <PersonalInformationInput data-testid="address-input" {...register("address")} />
              </EditFieldContainer>

              <EditFieldContainer>
                <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                  {t("settings.profile_information.postal")}
                </Typography>
                <PersonalInformationInput data-testid="zip-input" {...register("zip_code")} />
              </EditFieldContainer>

              <EditFieldContainer>
                <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                  {t("settings.profile_information.city")}
                </Typography>
                <PersonalInformationInput data-testid="city-input" {...register("city")} />
              </EditFieldContainer>

              {dropdownValues && (
                <EditFieldContainer>
                  <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                    {t("settings.profile_information.language")}
                  </Typography>
                  <StyledLanguageDropdown
                    m="0"
                    localiseLabels
                    placeholder={t("settings.profile_information.language")}
                    {...register("language_code")}
                    selected={profile.language_code}
                    options={dropdownValues}
                  />
                </EditFieldContainer>
              )}

              <EditFieldContainer>
                <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                  {t("settings.profile_information.mobile_number")}
                </Typography>
                <PersonalInformationInput data-testid="phone-input" {...register("phone_number")} />
              </EditFieldContainer>

              <EditFieldContainer>
                <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                  {t("settings.profile_information.birth_date")}
                </Typography>
                <StyledBirthdayDropdownContainer>
                  <StyledBirthdayDropdown
                    m="0"
                    selected={watchBirthMonth}
                    placeholder={t("common.month")}
                    {...register("birth_month")}
                    options={getDropdownMonths()}
                  />
                  <StyledBirthdayDropdown
                    m="0"
                    selected={watchBirthDate}
                    placeholder={t("common.day")}
                    {...register("birth_date")}
                    options={getDropdownDays()}
                  />
                  <StyledBirthdayDropdown
                    m="0"
                    selected={watchBirthYear}
                    {...register("birth_year")}
                    placeholder={t("common.year")}
                    options={getDropdownYears()}
                  />
                </StyledBirthdayDropdownContainer>
              </EditFieldContainer>
            </>
          ) : (
            <>
              <StyledTableRow>
                <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                  {t("form.first_name")}
                </Typography>
                <StyledParagraph data-testid="firstname-value">{profile.first_name}</StyledParagraph>
              </StyledTableRow>

              <StyledTableRow>
                <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                  {t("form.last_name")}
                </Typography>
                <StyledParagraph data-testid="lastname-value">{profile.last_name}</StyledParagraph>
              </StyledTableRow>

              <StyledTableRow>
                <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                  {t("settings.profile_information.address")}
                </Typography>
                <StyledParagraph data-testid="address-value">{profile.therapist_profile.address}</StyledParagraph>
              </StyledTableRow>

              <StyledTableRow>
                <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                  {t("settings.profile_information.postal")}
                </Typography>
                <StyledParagraph data-testid="zip-value">{profile.therapist_profile.zip_code}</StyledParagraph>
              </StyledTableRow>

              <StyledTableRow>
                <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                  {t("settings.profile_information.city")}
                </Typography>
                <StyledParagraph data-testid="city-value">{profile.therapist_profile.city}</StyledParagraph>
              </StyledTableRow>

              {dropdownValues && (
                <StyledTableRow>
                  <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                    {t("settings.profile_information.language")}
                  </Typography>
                  <StyledParagraph data-testid="lng-value">
                    {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                    {/* @ts-ignore */}
                    {t(`common.locales.${profile.language_code}`)}
                  </StyledParagraph>
                </StyledTableRow>
              )}

              <StyledTableRow>
                <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                  {t("settings.profile_information.mobile_number")}
                </Typography>
                <StyledParagraph data-testid="phone-value">{profile.therapist_profile.phone_number}</StyledParagraph>
              </StyledTableRow>

              <StyledTableRow>
                <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                  {t("settings.profile_information.birth_date")}
                </Typography>
                <StyledParagraph data-testid="birthday-value">{birthday && format(birthday, "PP")}</StyledParagraph>
              </StyledTableRow>
            </>
          )}
        </InfoTable>
        {isEditing && (
          <ButtonWrapper>
            <BaseButton
              type="button"
              onClick={handleChangePersonalInformationClick}
              text={t("buttons.cancel")}
              variant="secondary"
              size="large"
            />
            <BaseButton type="submit" text={t("buttons.save")} size="large" />
          </ButtonWrapper>
        )}
      </form>
    </WidgetBox>
  );
};

const PersonalInformationInput = styled(StyledInput)`
  ${fieldWithBorder};
  font: ${props => props.theme.font.link3};
  font-family: ${museoSansFont};
  color: ${props => props.theme.colors.redesign.db100};
  font-weight: 500;
`;

const StyledLanguageDropdown = styled(Dropdown)`
  width: 100%;
  select {
    font-family: ${museoSansFont};
    font-size: 14px;
  }
`;

const StyledBirthdayDropdownContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const StyledBirthdayDropdown = styled(Dropdown)`
  flex: 1 1 auto;
  min-width: 0;
  &:nth-child(1) {
    flex-basis: 70px;
  }
  &:nth-child(2) {
    flex-basis: 50px;
  }
  &:nth-child(3) {
    flex-basis: 90px;
  }
  select {
    font-family: ${museoSansFont};
    font-size: 14px;
  }
`;
